import { fork, call, put, takeEvery, select, all } from 'redux-saga/effects'
import { delay } from 'redux-saga'
import {
  find,
  pick,
  get,
  omit,
  isUndefined,
  isEmpty,
  isFunction,
  map,
  isNull,
} from 'lodash-es'
import {
  getFormValues,
  initialize as initializeForm,
} from 'redux-form/dist/redux-form'

import { showNotice } from '../../../Layout/Notices/Notices'

import {
  getSpeakers,
  addSpeaker,
  editSpeaker,
  editSpeakerAvatar,
  removeSpeakerAvatar,
  sendInviteEmail,
  deleteSpeaker,
  fetchSettings,
  reorderSpeakers,
} from './api'

const speakersCrudForm = 'speakersCrudForm'
// live_speaker_id is important so we know which one we edit
const formFields = [
  'live_speaker_id',
  'name',
  'email',
  'avatar_url',
  'title',
  'link',
  'company',
  'facebook',
  'twitter',
  'linkedin',
  'bio',
  'room_link',
  'hidden_p',
  'show_title_card_p',
]

const addHttpsToSpeakerWebsiteLink = (link) => {
  if (isEmpty(link) || /^(http(s)?)/gi.test(link)) {
    return link
  }

  return `https://${link}`
}

export default ({ actionNames, options, props, getFeatureRoot }) => {
  const {
    liveId,
    token,
    getMaxNumOfSpeakers = false,
    beforeUpdateHook,
    afterUpdateHook,
    isExcludingHidden,
    isIncludingHiddenGuestSpeakers,
  } = options
  const { onSpeakersInit, speakersFilter } = props

  function* afterUpdateHookWorker() {
    if (isFunction(afterUpdateHook)) {
      yield fork(afterUpdateHook)
    }
  }

  function* beforeUpdateHookWorker() {
    if (isFunction(beforeUpdateHook)) {
      yield fork(beforeUpdateHook)
    }
  }

  function* fetchSpeakers() {
    try {
      const { livespeakers } = yield call(
        getSpeakers,
        liveId,
        token,
        isExcludingHidden,
        isIncludingHiddenGuestSpeakers
      )

      const speakers = speakersFilter
        ? livespeakers.filter(speakersFilter)
        : livespeakers

      yield put({ type: actionNames.FETCH_SUCCESS, speakers })
    } catch (err) {
      yield put({ type: actionNames.FETCH_FAILURE })
      showNotice({
        message: 'Speakers data could not be loaded.',
        expirationMillis: 5e3,
        isClosable: true,
      })
    }
  }

  function* fetchSiteSettings() {
    if (!getMaxNumOfSpeakers) {
      return
    }

    try {
      const { settings } = yield call(fetchSettings)
      yield put({ type: actionNames.FETCH_SETTINGS_SUCCESS, settings })
    } catch (err) {
      showNotice({
        message: 'It will not be allowed adding new speakers due to an error.',
        expirationMillis: 5e3,
        isClosable: true,
      })
    }
  }

  function* updateSpeakerError(error) {
    yield put({ type: actionNames.INVITE_PRESENTER_FAIL })
    showNotice({
      message: `${error} speaker failed.`,
      expirationMillis: 5e3,
      isClosable: true,
    })
  }

  function* openCrudModalWorker(action) {
    const mode = get(action, 'payload[0]', 'new')
    const speakerId = get(action, 'payload[1]')
    const { speakers } = yield select(getFeatureRoot)
    const speaker = find(speakers, (one) => one.live_speaker_id === speakerId)

    const data = pick(speaker, formFields)

    switch (mode) {
      case 'edit':
        yield put(initializeForm(speakersCrudForm, data))
        break
      case 'delete':
        yield put(initializeForm(speakersCrudForm, { speakerId }))
        break
      default:
    }
  }

  function* addSpeakerWorker(formData) {
    try {
      const data = yield call(addSpeaker, liveId, {
        ...formData,
        send_invitation_p: 0,
      })
      yield fork(afterUpdateHookWorker)

      yield call(fetchSpeakers)

      const speakerId = get(data, 'livespeaker.live_speaker_id')
      yield put({ type: actionNames.SPEAKER_ADDED_SUCCESS, speakerId })
    } catch (err) {
      yield fork(updateSpeakerError, 'Saving')
    }
  }

  function* editSpeakerWorker(formData) {
    const fieldsToUpdate = omit(formData, ['avatarImageFile', 'avatar_url'])
    const speakerId = formData.live_speaker_id

    fieldsToUpdate.link = addHttpsToSpeakerWebsiteLink(fieldsToUpdate.link)

    const avatarImageFile = get(formData, 'avatarImageFile')
    try {
      if (isUndefined(avatarImageFile)) {
        // the avatar was not changed
        yield call(editSpeaker, fieldsToUpdate)
      } else if (!isNull(avatarImageFile)) {
        // if it is not null then it was changed
        yield all([
          call(editSpeaker, fieldsToUpdate),
          call(editSpeakerAvatar, speakerId, avatarImageFile),
        ])
      } else {
        // in any other case we will try to remove it
        yield all([
          call(editSpeaker, fieldsToUpdate),
          call(removeSpeakerAvatar, speakerId),
        ])
      }

      yield fork(afterUpdateHookWorker)
      yield fork(fetchSpeakers)

      yield put({ type: actionNames.SAVED_SUCCESS })
      yield delay(500)
      yield put({ type: actionNames.CLOSE_CRUD_MODAL })
    } catch (err) {
      yield fork(updateSpeakerError, 'Editing')
    }
  }

  function* deleteSpeakerWorker(speakerId) {
    try {
      yield call(deleteSpeaker, speakerId)

      yield fork(afterUpdateHookWorker)

      yield call(fetchSpeakers)
      yield put({ type: actionNames.SAVED_SUCCESS })
      yield delay(500)
      yield put({ type: actionNames.CLOSE_CRUD_MODAL })
    } catch (err) {
      showNotice({
        message: 'Deleting speaker failed',
        expirationMillis: 5e3,
        isClosable: true,
      })
    }
  }

  function* saveSpeakerWorker(action) {
    const { crudButtonState } = yield select(getFeatureRoot)

    if (crudButtonState === 'loading' || crudButtonState === 'success') {
      return
    }

    const formData = yield select(getFormValues(speakersCrudForm))
    const mode = get(action, 'payload[0]', 'new')

    yield put({ type: actionNames.SAVING })
    yield fork(beforeUpdateHookWorker)

    switch (mode) {
      case 'delete': {
        const speakerId = get(action, 'payload[1]')
        yield call(deleteSpeakerWorker, speakerId)
        break
      }
      case 'edit':
        yield call(editSpeakerWorker, formData)
        break
      default:
        // add speaker
        yield call(addSpeakerWorker, formData)
    }
  }

  function* sendInviteEmailWorker(action) {
    const { crudMode } = yield select(getFeatureRoot)
    const speakerId = get(action, 'payload[0]')
    const { sendInviteSpeakerId } = yield select(getFeatureRoot)
    if (!isNull(sendInviteSpeakerId)) {
      // in process of sending invite
      return
    }

    yield put({ type: actionNames.SEND_INVITE_LOADING, speakerId })

    try {
      yield call(sendInviteEmail, speakerId, liveId)
      yield fork(fetchSpeakers)
      if (crudMode !== 'speaker-added') {
        showNotice({
          message: 'The speaker invitation has been sent.',
          expirationMillis: 5e3,
          isClosable: true,
        })
      } else {
        yield put({ type: actionNames.SPEAKER_INVITED_SUCCESS })
      }
    } catch (err) {
      showNotice({
        message: 'The speaker invitation could not be sent.',
        expirationMillis: 5e3,
        isClosable: true,
      })
    } finally {
      yield put({ type: actionNames.SEND_INVITE_IDLE })
    }
  }

  function* reorderWorker(action) {
    const speakersOrdered = get(action, 'payload[0]')
    const { speakers } = yield select(getFeatureRoot)

    yield put({ type: actionNames.UPDATE_ORDER, speakers: speakersOrdered })
    const order = map(
      speakersOrdered,
      (speaker) => speaker.live_speaker_id
    ).join(',')

    yield fork(beforeUpdateHookWorker)

    try {
      yield call(reorderSpeakers, liveId, order)
      yield fork(afterUpdateHookWorker)
    } catch (err) {
      yield put({ type: actionNames.UPDATE_ORDER, speakers })
      showNotice({
        message: 'The new speakers order could not be set.',
        expirationMillis: 5e3,
        isClosable: true,
      })
    }
  }

  function* fetchSpeakersInit() {
    yield call(fetchSpeakers)
    if (isFunction(onSpeakersInit)) {
      onSpeakersInit()
    }
  }

  function* init() {
    yield fork(fetchSpeakersInit)
    yield fork(fetchSiteSettings)
    yield takeEvery(actionNames.SAVE, saveSpeakerWorker)
    yield takeEvery(actionNames.OPEN_CRUD_MODAL, openCrudModalWorker)
    yield takeEvery(actionNames.SEND_INVITE, sendInviteEmailWorker)
    yield takeEvery(actionNames.REORDER, reorderWorker)
    yield takeEvery(actionNames.REFETCH, fetchSpeakers)
  }

  return {
    default: [init()],
    init,
    fetchSpeakers,
    fetchSiteSettings,
    saveSpeakerWorker,
    deleteSpeakerWorker,
    openCrudModalWorker,
    editSpeakerWorker,
    addSpeakerWorker,
    sendInviteEmailWorker,
  }
}
